import { dateFromISOString } from '@wz-utilities/date.utilities';
import { DocumentApiModels } from 'models/api/document-api.models';
import { CommonModels } from '../../../../models/common.models';
import { DocumentModels } from '../../../../models/document.models';
import { DateProjector } from './date-projector';

/**************** RESPONSES ****************/
export function projectDocumentResponse(
  apiDocs: DocumentApiModels.GetDocumentResponse2,
): DocumentModels.Document2[] {
  return apiDocs.documents
    .sort((a, b) => (a.state <= b.state ? 1 : -1))
    .map((apiDoc) => {
      const doc: DocumentModels.Document2 = {
        customRejects: apiDoc.customRejects.map((reject) => ({
          message: reject,
          active: true,
        })),
        data: projectDocumentInformationResponse(apiDoc.type, apiDoc.data),
        fragments: apiDoc.fragments.map((res) => projectApiFragments(res)),
        id: apiDoc.id,
        predefinedRejects: apiDoc.predefinedRejects,
        state: apiDoc.state,
        type: apiDoc.type,
        allRejects: apiDoc.predefinedRejects
          .filter((pr) => pr.active)
          .map((pr) => pr.message)
          .concat(apiDoc.customRejects),
        expanded: true,
      };

      return doc;
    });
}

export function projectLegagyDocumentResponse(
  api: DocumentApiModels.GetDocumentResponse,
): DocumentModels.Document | any {
  if (!api) {
    return null;
  }
  return {
    ...api,
    fragments: api.fragments.map((f) => ({
      ...f,
      creationDate: dateFromISOString(f.creationDate),
    })),
  };
}

export function projectApiFragments(
  fragment: DocumentApiModels.Fragment2,
): DocumentModels.Fragment2 | any {
  return {
    ...fragment,
    creationDate: dateFromISOString(fragment.creationDate),
    printDate: dateFromISOString(fragment.printDate!),
    registerLetterExecutionDate: dateFromISOString(
      fragment.registerLetterExecutionDate!,
    ),
    brokerVisible: fragment.brokerVisible,
    printRequest: fragment.printRequest,
  };
}

export function projectDocumentInformationResponse(
  docType: DocumentModels.DocumentType,
  docData: DocumentModels.DocumentInformation,
): DocumentModels.DocumentInformation | any {
  if (!docData) {
    return null;
  }

  switch (docType) {
    case DocumentModels.DocumentType.PermisDeConduire:
      return projectLicenseInformationResponse(
        docData as DocumentModels.LicenseInformation,
      );
    case DocumentModels.DocumentType.CarteGrise:
      return projectCarteGriseInformationResponse(
        docData as DocumentApiModels.CarteGriseInformation,
      );
    case DocumentModels.DocumentType.ReleveInformation:
      return projectReleveInformationInformationResponse(
        docData as DocumentApiModels.ReleveInformationInformation,
      );
    default:
      return docData;
  }
}

export function projectCarteGriseInformationResponse(
  cgInfos: DocumentApiModels.CarteGriseInformation,
): DocumentModels.CarteGriseInformation {
  return {
    titulaireCarteGrise: cgInfos.titulaireCarteGrise,
    car: {
      codeGta: cgInfos.codeGta,
      dateAchatVehicule: DateProjector.fromApiToApp(cgInfos.dateAchatVehicule)!,
      dateMiseEnCirculation: DateProjector.fromApiToApp(
        cgInfos.dateMiseEnCirculation,
      )!,
      immatriculation: cgInfos.immatriculation,
      typeMine: cgInfos.typeMine,
    },
  };
}

export function projectLicenseInformationResponse(
  permisInformation: DocumentModels.LicenseInformation,
): DocumentModels.LicenseInformation {
  return {
    dateNaissance: DateProjector.fromApiToApp(permisInformation.dateNaissance)!,
    datePermis: DateProjector.fromApiToApp(permisInformation.datePermis)!,
    typePermis: permisInformation.typePermis,
  };
}

export function projectReleveInformationInformationResponse(
  riInfo: DocumentModels.ReleveInformationInformation,
): DocumentModels.ReleveInformationInformation | any {
  return {
    bonus: riInfo.bonus,
    nombreAnneeAssurance: riInfo.nombreAnneeAssurance,
    nombreAnneeBonus50: riInfo.nombreAnneeBonus50,
    periode36Mois: riInfo.periode36Mois,
    periodeInterruption: riInfo.periodeInterruption,
    raisonResiliation: riInfo.raisonResiliation,
    periodes: riInfo.periodes
      ? riInfo.periodes.map((p) => ({
          start: DateProjector.fromApiToApp(p.start),
          end: DateProjector.fromApiToApp(p.end),
          vehicule: p.vehicule,
          assure: p.assure,
          illisible: p.illisible,
          incomplet: p.incomplet,
        }))
      : [],
  };
}

/**************** REQUESTS ****************/

export function projectDocumentDataRequest(
  docType: DocumentModels.DocumentType,
  docData: DocumentModels.DocumentFormData,
): DocumentApiModels.DocumentDataRequest {
  return {
    customRejects: docData.customRejects
      .filter((cr) => cr.active)
      .map((cr) => cr.message),
    data: projectDocumentInformationRequest(docType, docData.data),
    predefinedRejects: docData.predefinedRejects
      .filter((pr) => pr.active)
      .map((pr) => pr.id),
    state: docData.state,
  };
}

export function projectDocumentInformationRequest(
  docType: DocumentModels.DocumentType,
  docData: DocumentModels.DocumentInformation,
): DocumentModels.DocumentInformation {
  switch (docType) {
    case DocumentModels.DocumentType.PermisDeConduire:
      return projectLicenseInformationRequest(
        docData as DocumentModels.LicenseInformation,
      );
    case DocumentModels.DocumentType.CarteGrise:
      return projectCarteGriseInformationRequest(
        docData as DocumentModels.CarteGriseInformation,
      );
    case DocumentModels.DocumentType.ReleveInformation:
      return projectReleveInformationInformationRequest(
        docData as DocumentModels.ReleveInformationInformation,
      );
    default:
      return docData;
  }
}

export function projectCarteGriseInformationRequest(
  cgInfos: DocumentModels.CarteGriseInformation,
): DocumentApiModels.CarteGriseInformation | any {
  const { car } = cgInfos;
  return {
    codeGta: car ? car.codeGta : null,
    dateAchatVehicule: car
      ? DateProjector.fromAppToApi(car.dateAchatVehicule)
      : null,
    dateMiseEnCirculation: car
      ? DateProjector.fromAppToApi(car.dateMiseEnCirculation)
      : null,
    titulaireCarteGrise: cgInfos.titulaireCarteGrise,
    typeMine: car ? car.typeMine : null,
    immatriculation: car ? car.immatriculation : null,
  };
}

export function projectLicenseInformationRequest(
  permisInformation: DocumentModels.LicenseInformation,
): DocumentModels.LicenseInformation {
  return {
    dateNaissance: DateProjector.fromAppToApi(permisInformation.dateNaissance)!,
    datePermis: DateProjector.fromAppToApi(permisInformation.datePermis)!,
    typePermis: permisInformation.typePermis,
  };
}

export function projectReleveInformationInformationRequest(
  riInfo: DocumentModels.ReleveInformationInformation,
): DocumentModels.ReleveInformationInformation | any {
  return {
    bonus: riInfo.bonus,
    nombreAnneeAssurance: riInfo.nombreAnneeAssurance,
    nombreAnneeBonus50: riInfo.nombreAnneeBonus50,
    periode36Mois: riInfo.periode36Mois,
    periodeInterruption: riInfo.periodeInterruption,
    raisonResiliation: riInfo.raisonResiliation,
    periodes: riInfo.periodes
      ? riInfo.periodes.map((p) => ({
          start: DateProjector.fromAppToApi(p.start),
          end: DateProjector.fromAppToApi(p.end),
          vehicule: p.vehicule,
          assure: p.assure,
          illisible: p.illisible,
          incomplet: p.incomplet,
        }))
      : [],
  };
}

export class DocumentProjector {
  static projectDocumentStatus(
    status: DocumentModels.DocumentStateEnum,
  ): CommonModels.StatusEnum {
    switch (status) {
      case DocumentModels.DocumentStateEnum.ToProcess:
        return CommonModels.StatusEnum.Default;
      case DocumentModels.DocumentStateEnum.Incomplete:
        return CommonModels.StatusEnum.PartiallyValid;
      case DocumentModels.DocumentStateEnum.Full:
        return CommonModels.StatusEnum.Valid;
      case DocumentModels.DocumentStateEnum.NotFound:
        return CommonModels.StatusEnum.Invalid;
      default:
        return CommonModels.StatusEnum.Invalid;
    }
  }

  static projectDocumentStatusForBroker(
    status: DocumentModels.DocumentStateEnum,
  ): CommonModels.StatusEnum {
    switch (status) {
      // Not found or incomplete : red
      case DocumentModels.DocumentStateEnum.NotFound:
      case DocumentModels.DocumentStateEnum.Archived:
      case DocumentModels.DocumentStateEnum.Incomplete:
        return CommonModels.StatusEnum.Invalid;
      // Full : green
      case DocumentModels.DocumentStateEnum.Full:
        return CommonModels.StatusEnum.Valid;
      // ToProcess : orange (waiting for validation)
      case DocumentModels.DocumentStateEnum.ToProcess:
        return CommonModels.StatusEnum.PartiallyValid;
      default:
        return CommonModels.StatusEnum.Default;
    }
  }
}
